import React, { useState, useEffect, useMemo } from "react";

import MDTypography from "components/MDTypography";
import Autocomplete from "@mui/material/Autocomplete";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import EventCalendar from "examples/Calendar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { setCurrentDevices } from "../../../../redux/reducers/devicesReducer";
import { setCurrentOrder } from "../../../../redux/reducers/ordersReducer";
import { useDispatch } from "react-redux";

import { useSelector } from "react-redux";
import { getOrder } from "../../../../api/orders";
import { getDevice } from "../../../../api/devices";
import { getStage } from "../../../../api/stages";
import { getAbsences } from "../../../../api/absences";

import CreateEditAbsences from "../../CreateEditAbsences";
import CreateEditStages from "../../../devicesComponents/CreateEditStages";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import useAuthCheck from "config/useAuthCheck";
import { useAuth } from "context/AuthContext";

export default function Individual() {
  useAuthCheck();
  const { user } = useAuth();
  const allUsers = useSelector((state) => state.users.allUsers);
  const dispatch = useDispatch();

  const [stages, setStages] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [userId, setUserId] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentAbsence, setCurrentAbsence] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [currentMission, setCurrentMission] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [missionEditOpen, setMissionEditOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  async function fetchStages(userId) {
    const response = await getStage(userId, true);
    const stagesWithAdditionalInfo = await Promise.all(
      response.data.map(async (stage) => {
        const deviceId = await fetchOrderId(stage.device_id);
        const orderShadowDeleted = await fetchOrderDeleted(deviceId);
        return {
          ...stage,
          order_id: orderShadowDeleted,
        };
      })
    );
    const filteredStages = stagesWithAdditionalInfo.filter((stage) => stage.order_id !== undefined);
    setStages(filteredStages);
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    dispatch(setCurrentDevices([]));
    dispatch(setCurrentOrder(null));
  }, [dispatch]);

  async function fetchAbsences(userId) {
    const response = await getAbsences(userId);
    setAbsences(response.data);
  }

  async function fetchOrderDeleted(deviceId) {
    const response = await getOrder(deviceId);
    return response.data.order_id;
  }

  async function fetchOrderId(deviceId) {
    const response = await getDevice(deviceId);
    return response.data.order_id;
  }

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  useEffect(() => {
    if (user && (user.role === "Technicien" || user.role === "Sous-traitant")) {
      setUserId({ id: user.user_id, label: `${user.first_name} ${user.last_name}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user && userId !== null) {
      fetchStages(userId.id);
      fetchAbsences(userId.id);
    } else {
      setCalendarEvents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user]);

  useEffect(() => {
    setCalendarEvents([]);
    setCalendarEvents(generateEvents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages, absences, darkMode]);

  function generateEvents() {
    let absencesEndDate = "";
    let stagesEndDate = "";
    const events = [];
    for (let i = 0; i < absences.length; i++) {
      absencesEndDate = new Date(
        absences[i].absence_end_date !== null && absences[i].absence_end_date.substring(0, 10)
      );
      absencesEndDate.setDate(absencesEndDate.getDate() + 1);
      events.push({
        id: `#${absences[i].absence_id}`,
        title:
          absences[i].absence_description !== null
            ? absences[i].absence_description === "Déplacement"
              ? `${absences[i].absence_description} ${
                  absences[i].trip_area === null ? "" : "en " + absences[i].trip_area
                }`
              : absences[i].absence_description
            : "Description de l'absence inconnue",
        start:
          absences[i].absence_start_date !== null &&
          absences[i].absence_start_date.substring(0, 10),
        end:
          absences[i].absence_end_date !== null &&
          `${absencesEndDate.getFullYear()}-${
            absencesEndDate.getMonth() + 1 < 10
              ? "0" + (absencesEndDate.getMonth() + 1)
              : absencesEndDate.getMonth() + 1
          }-${
            absencesEndDate.getDate() < 10
              ? "0" + absencesEndDate.getDate()
              : absencesEndDate.getDate()
          }`,
        className:
          absences[i].absence_description === "Congé" || absences[i].absence_description === "Arrêt"
            ? "error"
            : "warning",
      });
    }
    for (let i = 0; i < stages.length; i++) {
      stagesEndDate = new Date(
        stages[i].stage_end_date !== null && stages[i].stage_end_date.substring(0, 10)
      );
      stagesEndDate.setDate(stagesEndDate.getDate() + 1);
      events.push({
        id: `${stages[i].stage_id}|||${stages[i].order_id}|||${stages[i].stage_foreman_id}`,
        title:
          stages[i].stage_description !== null
            ? `${stages[i].stage_device_number} - ${stages[i].stage_description}`
            : `${stages[i].stage_device_number} - Description de la phase inconnue`,
        start: stages[i].stage_start_date !== null && stages[i].stage_start_date.substring(0, 10),
        end:
          stages[i].stage_end_date !== null &&
          `${stagesEndDate.getFullYear()}-${
            stagesEndDate.getMonth() + 1 < 10
              ? "0" + (stagesEndDate.getMonth() + 1)
              : stagesEndDate.getMonth() + 1
          }-${
            stagesEndDate.getDate() < 10 ? "0" + stagesEndDate.getDate() : stagesEndDate.getDate()
          }`,
        className: "success",
      });
    }
    return events;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={10000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          variant="filled"
          severity="warning"
          sx={{
            width: "100%",
            color: "white !important",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {user && (
        <MDBox pt={4} pl={0} sx={{ height: "max-content", textAlign: "right" }}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={1.5}
                px={2}
                variant="gradient"
                borderRadius="lg"
                sx={{
                  background: !darkMode ? "linear-gradient(195deg, #364c70, #273956)" : "white",
                  boxShadow: darkMode ? "0 1px 4px 1px white" : "0 1px 4px 1px #344767",
                  verticalAlign: "middle",
                }}
              >
                <MDTypography
                  variant="h4"
                  style={{
                    textAlign: "center",
                    lineHeight: "2.4rem",
                    verticalAlign: "middle",
                  }}
                  color={darkMode ? "inherit" : "white"}
                >
                  Planning individuel
                </MDTypography>
              </MDBox>
              <Grid container xs={12} p={4} spacing={3}>
                <Grid item lg={3} xs={12}>
                  <Tooltip
                    title={
                      user && (user.role === "Sous-traitant" || user.role === "Technicien")
                        ? "Vous n'avez pas les droits pour modifier cette information"
                        : ""
                    }
                    placement="top"
                  >
                    <Autocomplete
                      disabled={
                        user && (user.role === "Sous-traitant" || user.role === "Technicien")
                      }
                      sx={{ width: "100%" }}
                      options={allUsers}
                      value={userId}
                      onChange={(event, newUserId) => {
                        if (newUserId === null) {
                          setUserId(null);
                        } else {
                          setUserId(newUserId);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput {...params} label="Chosissez un utilisateur" variant="outlined" />
                      )}
                    />
                  </Tooltip>
                </Grid>

                {userId &&
                  user &&
                  (user.role === "Admin" ||
                    user.role === "Pilote" ||
                    user.role === "Chef d'agence" ||
                    user.role === "Contremaître") && (
                    <Grid item lg={3} xs={12}>
                      <CreateEditAbsences edit={false} setAbsences={setAbsences} userId={userId} />
                      {currentAbsence && (
                        <CreateEditAbsences
                          edit
                          setAbsences={setAbsences}
                          absence={currentAbsence}
                          userId={userId}
                          editOpen={editOpen}
                          setEditOpen={setEditOpen}
                        />
                      )}
                      {missionEditOpen && (
                        <CreateEditStages
                          edit
                          setStages={setStages}
                          stages={currentMission}
                          editOpen={missionEditOpen}
                          setEditOpen={setMissionEditOpen}
                          currentOrderId={currentOrderId}
                          global={true}
                          userId={userId.id}
                          renderStep={false}
                        />
                      )}
                    </Grid>
                  )}

                {calendarEvents && calendarEvents.length === 0 ? (
                  <Grid
                    item
                    lg={
                      userId &&
                      user &&
                      (user.role === "Admin" ||
                        user.role === "Pilote" ||
                        user.role === "Chef d'agence" ||
                        user.role === "Contremaître")
                        ? 6
                        : 9
                    }
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    {userId ? (
                      <MDBox sx={{ textAlign: "center" }}>
                        <MDTypography
                          component="a"
                          variant="h5"
                          color={darkMode ? "white" : "inherit"}
                          style={{
                            verticalAlign: "middle",
                          }}
                        >
                          Le planning de cet utilisateur est vide
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <MDBox sx={{ textAlign: "center" }}>
                        <MDTypography
                          component="a"
                          variant="h5"
                          color={darkMode ? "white" : "inherit"}
                          style={{
                            verticalAlign: "middle",
                          }}
                        >
                          Aucun utilisateur n'a été selectionné
                        </MDTypography>
                      </MDBox>
                    )}
                  </Grid>
                ) : (
                  <Grid
                    item
                    lg={
                      userId &&
                      user &&
                      (user.role === "Admin" ||
                        user.role === "Pilote" ||
                        user.role === "Chef d'agence" ||
                        user.role === "Contremaître")
                        ? 6
                        : 9
                    }
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <MDBox sx={{ textAlign: "center" }}>
                      <MDTypography
                        component="a"
                        variant="body1"
                        color={darkMode ? "white" : "inherit"}
                        style={{
                          verticalAlign: "middle",
                        }}
                      >
                        {user &&
                        user.role !== "Admin" &&
                        user.role !== "Pilote" &&
                        user.role !== "Contremaître"
                          ? "Naviguez sur le calendrier pour y découvrir vos évenements 🙂"
                          : "Cliquez sur les évenements du calendrier pour les modifier 😉"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                )}
              </Grid>
              <MDBox paddingRight={4} paddingBottom={4} paddingLeft={4}>
                <Grid
                  xs={12}
                  marginTop={1.5}
                  sx={{
                    height: "max-content",
                    ".fc-event-main": { cursor: "pointer" },
                  }}
                >
                  <EventCalendar
                    initialView="dayGridMonth"
                    events={calendarEvents}
                    eventClick={function (info) {
                      if (
                        user &&
                        (user.role === "Admin" ||
                          user.role === "Pilote" ||
                          user.role === "Chef d'agence" ||
                          user.role === "Contremaître")
                      ) {
                        if (!info.event.id.includes("#")) {
                          const currentStageId = parseInt(info.event.id.split("|||")[0]);
                          const currentStageOrderId = info.event.id.split("|||")[1];
                          const currentStageOrderForemanId = parseInt(
                            info.event.id.split("|||")[2]
                          );
                          if (
                            user &&
                            (user.role === "Admin" ||
                              user.role === "Pilote" ||
                              user.role === "Chef d'agence" ||
                              (user.role === "Contremaître" &&
                                currentStageOrderForemanId === user.user_id))
                          ) {
                            setCurrentMission(
                              stages.find((stage) => stage.stage_id === currentStageId)
                            );
                            setCurrentOrderId(currentStageOrderId);
                            setMissionEditOpen(true);
                          } else if (
                            user &&
                            user.role === "Contremaître" &&
                            currentStageOrderForemanId !== user.user_id
                          ) {
                            setSnackbarOpen(true);
                            setSnackbarMessage(
                              "Vous n'avez pas les droits pour accéder à cette phase"
                            );
                          }
                        } else {
                          const currentAbsenceId = parseInt(info.event.id.slice(1));
                          setCurrentAbsence(
                            absences.find((absence) => absence.absence_id === currentAbsenceId)
                          );
                          setEditOpen(true);
                        }
                      }
                    }}
                    selectable
                  />
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </MDBox>
      )}
    </DashboardLayout>
  );
}
